import React, { useContext, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Multistep, { IMultistepComponent } from 'components/multistep/Multistep'
import { LifeCycleStatus } from 'constants/enums/lifeCycleStatus'
import { ApplicationDispatchContext } from 'contexts/ApplicationContext'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import { CheckIsLifeCycleStatus } from 'utils/loanHelpers'
import { Decision } from 'constants/enums/decision'
import ExternalServices from 'services/externalServices'
import { VirtualPagePaths } from 'constants/values/gtmConstants'
import { UrlStatus } from 'constants/enums/signingUrlTypes'
import LoanOfferIt from 'components/application-blocks/application-offer-blocks/LoanOffer/LoanOfferIt'
import { FillPersonalDataIt } from 'components/application-blocks/application-offer-blocks/FillPersonalData/FillPersonalDataIt'
import { SignPsd2It } from 'components/application-blocks/application-offer-blocks/SignPsd2/SignPsd2It'
import { SignPnIt } from 'components/application-blocks/application-offer-blocks/SignPn/SignPnIt/SignPnIt'
import ThankYou from 'components/application-blocks/common-blocks/ThankYou/ThankYou'
import { UploadDocumentsIt } from 'components/application-blocks/application-offer-blocks/UploadDocuments/UploadDocumentsIt'
import { InfoBox } from 'components/message-boxes/information-box/InformationBox'
import appConfig from 'app.config'


export const LoanApplicationStepsIt = () => {
	const { loanApplication }: IApplicationContext = useContext(
		ApplicationDispatchContext,
	)
	const { t } = useTranslation()

	const loanApplicationSteps: IMultistepComponent[] = [
		{
			id: 1,
			header: 'FillPersonalData',
			body: <FillPersonalDataIt />,
			show: CheckIsLifeCycleStatus(
				[LifeCycleStatus.MultiStepUpdatePersonalData],
				loanApplication.applicationData?.lifeCycleStatus!,
			),
			stepCompleted: false,
			virtualPagePath: VirtualPagePaths.IT.PERSONAL_DATA,
		},
		{
			id: 2,
			header: 'PSD2',
			body: <SignPsd2It />,
			show: CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.MultiStepUpdatePersonalData,
					LifeCycleStatus.WaitingForBankData,
				],
				loanApplication.applicationData?.lifeCycleStatus!,
			),
			stepCompleted: false,
			virtualPagePath: VirtualPagePaths.IT.PSD2,
		},
		{
			id: 3,
			header:
				loanApplication.applicationData?.decision ===
					Decision.Approved &&
				loanApplication.applicationData?.offer !== undefined &&
				loanApplication.applicationData?.offer !== null
					? 'UpdateLoanOfferHeader'
					: 'UpdateBankInfoHeader',
			body: <LoanOfferIt />,
			show: CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.MultiStepUpdatePersonalData,
					LifeCycleStatus.WaitingForBankData,
					LifeCycleStatus.PrintPromissoryNote,
				],
				loanApplication.applicationData?.lifeCycleStatus!,
			),
			stepCompleted: false,
			virtualPagePath: VirtualPagePaths.IT.LOAN_OFFER,
		},
		{
			id: 4,
			header: 'SignPn',
			body: <SignPnIt />,
			show:
				loanApplication.applicationData?.signingUrls.pnSigning
					.status !== UrlStatus.Signed &&
				CheckIsLifeCycleStatus(
					[
						LifeCycleStatus.ReceiveSigning,
						LifeCycleStatus.MultiStepUpdatePersonalData,
						LifeCycleStatus.WaitingForBankData,
						LifeCycleStatus.PrintPromissoryNote,
					],
					loanApplication.applicationData?.lifeCycleStatus!,
				),
			stepCompleted: false,
			virtualPagePath: VirtualPagePaths.IT.SIGN_PN,
		},
		{
			id: 5,
			header: 'UploadDocument',
			body: <UploadDocumentsIt />,
			show: CheckIsLifeCycleStatus(
				[LifeCycleStatus.DocumentsUpload],
				loanApplication.applicationData?.lifeCycleStatus!,
			),
			stepCompleted: false,
			virtualPagePath: VirtualPagePaths.IT.UPLOAD_DOCUMENTS,
		},
		{
			id: 6,
			header: 'ThankYouHeader',
			body: <ThankYou />,
			show: CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.MultiStepUpdatePersonalData,
					LifeCycleStatus.WaitingForBankData,
					LifeCycleStatus.PrintPromissoryNote,
					LifeCycleStatus.ReceiveSigning,
					LifeCycleStatus.CustomerVerification,
					LifeCycleStatus.InternalCheck,
					LifeCycleStatus.DocumentsUpload,
					LifeCycleStatus.SendToBo,
				],
				loanApplication.applicationData?.lifeCycleStatus!,
			),
			stepCompleted: false,
			virtualPagePath: VirtualPagePaths.IT.THANK_YOU,
		},
		{
			id: 7,
			header: 'DisbursedHeader',
			body: (
				<InfoBox
					infoType="info"
					css={'application-status-box-info'}
					message={t('StatusTextDisbursed', {
						company: appConfig.companyName,
					})}
				/>
			),
			show: CheckIsLifeCycleStatus(
				[LifeCycleStatus.Disbursed],
				loanApplication.applicationData?.lifeCycleStatus!,
			),
			stepCompleted: false,
			virtualPagePath: VirtualPagePaths.IT.DISBURSED,
		},
		{
			id: 8,
			header: 'ApplicationReviewedHeader',
			body: (
				<InfoBox
					infoType="info"
					css={'application-status-box-info'}
					message={t('CreditDecisionInvestigation')}
				/>
			),
			show: CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.ApplicationReceived,
					LifeCycleStatus.CreditDecisionError,
					LifeCycleStatus.CreditDecisionInvestigation,
				],
				loanApplication.applicationData?.lifeCycleStatus!,
			),
			stepCompleted: false,
			virtualPagePath: VirtualPagePaths.IT.REVIEWED,
		},
	]

	const handleStepChange = useCallback(
		(step: IMultistepComponent) => {
			if (step.virtualPagePath) ExternalServices.pushVirtualPageView(step.virtualPagePath)
		},
		[],
	)

	return (
		<Multistep
			multisteps={loanApplicationSteps}
			resetInitialState={false}
			onStepChange={handleStepChange}
		/>
	)
}
